/* eslint-disable react/jsx-sort-props */
import { Locale } from "@dealer-center/ddc-core-utils";
import type {
  InterstateOnChangeEvent,
  SelectInputEventValue
} from "@dealer-center/core-components";
import type { IncentivesPayload } from "@dealer-center/ddc-pages-incentives-manager-interface";
import {
  Button,
  SearchInput,
  SelectInput,
  Tooltip
} from "@dealer-center/core-components";
import "./TableNavigation.scss";
import type { Filters } from "../utils/navFilterUtils";
import {
  getMakes,
  getModels,
  getTrims,
  getYears
} from "../utils/navFilterUtils";
import { defaultFilters } from "../providers/FiltersProvider";
import type { RootNode } from "@dealer-center/ddc-pages-incentives-manager-lambda";
import { MessagePage } from "@dealer-center/core-custom-components";
import { useContext, useMemo, useRef, useState } from "react";
import { parseFreeText } from "../utils/freeSearch";
import {
  getIncentiveTypeLabel,
  getIncentiveTypes,
  getUniqueCreatorAccountNames
} from "../utils/utils";
import { PreferencesContext } from "../providers/PreferencesProvider";

export const formatFilterItems = (items, isExcludingAll = false) => {
  const formattedItems = isExcludingAll
    ? []
    : [
        {
          disabled: false,
          label: Locale.getText("ALL") as string,
          value: "ALL"
        }
      ];
  return formattedItems.concat(
    items.map(item => {
      return {
        disabled: false,
        label: typeof item === "number" ? JSON.stringify(item) : item,
        value: typeof item === "number" ? JSON.stringify(item) : item
      };
    })
  );
};

export const clearLowerFilters = (filters, level = 0, skipMake = false) => {
  const clonedFilters = { ...filters };
  const fields = Object.keys(clonedFilters);
  for (let i = fields.length - 1; i >= level; i--) {
    if (
      (fields[i] === "make" && skipMake) ||
      ["years", "freeWords", "scratchText"].includes(fields[i])
    ) {
      continue; // Skip clearing make if skipMake is true
    }
    clonedFilters[fields[i]] = null;
  }
  return clonedFilters;
};

export const TableNavigation = ({
  accountMakes,
  filters,
  navtreeData,
  setFilters,
  makeRequired = false,
  disableMake = false,
  isMakeLocked,
  showClearFilters = true,
  isLoading = false,
  incentives
}: {
  accountMakes: string[];
  filters: Filters;
  navtreeData: RootNode | null;
  setFilters: (filters: Filters) => void;
  makeRequired?: boolean;
  disableMake?: boolean;
  isMakeLocked?: (make: string) => boolean | undefined;
  showClearFilters?: boolean;
  isLoading?: boolean;
  incentives: null | IncentivesPayload;
}) => {
  const { preferences } = useContext(PreferencesContext);
  const { isFordDirect } = preferences;
  const [searchText, setSearchText] = useState<string>("");
  const [searchKey, setSearchKey] = useState<number>(0);
  const typeOptions = useMemo(
    () => [
      {
        label: Locale.getText("ALL") as string,
        value: "ALL",
        disabled: false
      },
      ...getIncentiveTypes().map(type => ({
        label: getIncentiveTypeLabel(type, isFordDirect),
        value: type,
        disabled: false
      }))
    ],
    [isFordDirect]
  );
  const vehicleConditions = [
    {
      disabled: false,
      label: Locale.getText("ALL") as string,
      value: "ALL"
    },
    {
      disabled: false,
      label: Locale.getText("NEW") as string,
      value: "NEW"
    },
    {
      disabled: false,
      label: Locale.getText("CERTIFIED_INVENTORY") as string,
      value: "CPO"
    }
  ];

  const applyFilters = (
    filters: Filters,
    event: InterstateOnChangeEvent<SelectInputEventValue>,
    field = ""
  ) => {
    const clonedFilters = { ...filters };
    if (field === "year") {
      clonedFilters[field] =
        event.target.value === "ALL"
          ? null
          : parseInt(event.target.value as string, 10);
    } else {
      clonedFilters[field] =
        event.target.value === "ALL" ? null : (event.target.value as string);
    }
    setFilters(clonedFilters);
  };

  const originalIncentivesRef = useRef<IncentivesPayload | null>(null);

  if (incentives && !originalIncentivesRef.current) {
    originalIncentivesRef.current = incentives;
  }
  const incentiveSource = useMemo(() => {
    if (!originalIncentivesRef.current) return [];

    return [
      {
        label: Locale.getText("ALL") as string,
        value: "ALL",
        disabled: false
      },
      ...getUniqueCreatorAccountNames(originalIncentivesRef.current).map(
        source => ({
          label: source,
          value: source,
          disabled: false
        })
      )
    ];
  }, []); // if dependacy exists source drop down list will update as sources are selected removing options

  const doSearch = (text: string) => {
    const cleanText = text ? text.trim() : "";
    if (cleanText !== searchText) {
      const freeTextFilter = parseFreeText(
        cleanText,
        accountMakes || [],
        filters,
        navtreeData
      );
      setFilters(freeTextFilter);
      setSearchKey(prev => prev + 1); // Needed to force changing of search input value
      setSearchText(
        freeTextFilter.freeWords
          ? (
              freeTextFilter.scratchText +
              " " +
              freeTextFilter.freeWords.join(" ")
            )
              .trim()
              .replace(/\s\s+/g, " ")
          : freeTextFilter.scratchText
      );
    }
  };

  if (isLoading) {
    return (
      <div className="table-navigation">
        <MessagePage
          actionName={Locale.getText("LOADING") as string}
          message={Locale.getText("LOADING") as string}
        />
      </div>
    );
  }

  return (
    <div className="table-navigation">
      <div className="navigation-items">
        <Tooltip
          position="right"
          size="large"
          toolTipContent={Locale.getText("SEARCH_TOOLTIP") as string}
        >
          <SearchInput
            key={searchKey}
            inputValue={searchText}
            placeholder={Locale.getText("SEARCH") as string}
            size={"small"}
            onClearCallback={() => {
              if (searchText !== "") {
                setFilters({
                  ...filters,
                  years: null,
                  freeWords: null,
                  scratchText: ""
                });
                setSearchText("");
              }
            }}
            onSearchClick={value => {
              doSearch(value.target.value as string);
            }}
          />
        </Tooltip>
        <SelectInput
          displayDeselectOption={false}
          id={`for-nav-condition${makeRequired ? "-extra" : ""}`}
          label={Locale.getText("VEHICLE_CONDITION") as string}
          name={Locale.getText("VEHICLE_CONDITION") as string}
          options={vehicleConditions}
          value={filters.condition ? filters.condition : "ALL"}
          onChange={event => {
            applyFilters(
              clearLowerFilters(filters, 1, makeRequired),
              event,
              "condition"
            );
          }}
        />
        <SelectInput
          disabled={disableMake}
          displayDeselectOption={false}
          id={`for-nav-make${makeRequired ? "-extra" : ""}`}
          label={Locale.getText("MAKE") as string}
          name={Locale.getText("MAKE") as string}
          options={formatFilterItems(
            getMakes(navtreeData, { ...filters, year: null }, make => {
              if (isMakeLocked) {
                return isMakeLocked(make);
              }
              return false;
            }),
            makeRequired
          )}
          value={filters.make ? filters.make : "ALL"}
          onChange={event => {
            applyFilters(clearLowerFilters(filters, 2), event, "make");
          }}
        />
        <SelectInput
          disabled={filters.make === null}
          displayDeselectOption={false}
          id={`for-nav-year${makeRequired ? "-extra" : ""}`}
          label={Locale.getText("YEAR") as string}
          name={Locale.getText("YEAR") as string}
          options={formatFilterItems(
            getYears(navtreeData, { ...filters, model: null })
          )}
          value={filters.year ? String(filters.year) : "ALL"}
          onChange={event => {
            applyFilters(clearLowerFilters(filters, 3), event, "year");
          }}
        />
        <SelectInput
          disabled={filters.make === null || filters.year === null}
          displayDeselectOption={false}
          id={`for-nav-model${makeRequired ? "-extra" : ""}`}
          label={Locale.getText("MODEL") as string}
          name={Locale.getText("MODEL") as string}
          options={formatFilterItems(
            getModels(navtreeData, { ...filters, trim: null })
          )}
          value={filters.model ? filters.model : "ALL"}
          onChange={event => {
            applyFilters(clearLowerFilters(filters, 4), event, "model");
          }}
        />
        <SelectInput
          disabled={
            filters.make === null ||
            filters.year === null ||
            filters.model === null
          }
          displayDeselectOption={false}
          id={`for-nav-trim${makeRequired ? "-extra" : ""}`}
          label={Locale.getText("TRIM") as string}
          name={Locale.getText("TRIM") as string}
          options={formatFilterItems(getTrims(navtreeData, filters))}
          value={filters.trim ? filters.trim : "ALL"}
          onChange={event => {
            applyFilters(clearLowerFilters(filters, 5), event, "trim");
          }}
        />
        <SelectInput
          displayDeselectOption={false}
          id={`for-nav-type${makeRequired ? "-extra" : ""}`}
          label={Locale.getText("TYPE") as string}
          name={Locale.getText("TYPE") as string}
          options={typeOptions}
          value={filters.type ? filters.type : "ALL"}
          onChange={event => {
            applyFilters(filters, event, "type");
          }}
        />
        <SelectInput
          displayDeselectOption={false}
          id={`for-nav-source${makeRequired ? "-extra" : ""}`}
          label={Locale.getText("SOURCE") as string}
          name={Locale.getText("SOURCE") as string}
          options={incentiveSource}
          value={filters.source ? filters.source : "ALL"}
          onChange={event => {
            applyFilters(filters, event, "source");
          }}
        />
        {showClearFilters && (
          <Button
            analyticsConfiguration={{
              eventName: "",
              eventProperties: undefined
            }}
            buttonStyle="secondary"
            onClick={() => {
              const make = makeRequired ? { make: filters.make } : {};
              setFilters({ ...defaultFilters, ...make });
              setSearchText("");
            }}
          >
            {Locale.getText("CLEAR_FILTERS")}
          </Button>
        )}
      </div>
    </div>
  );
};
